import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { cn } from '@/lib/utils.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { ConnectionChoiceSelect } from './ConnectionChoiceSelect.js';
import { useConnectorScope } from './connector-scope.js';

/**
 * Xray Cloud is api-key only — a client id + client secret pair created in
 * Xray Global Settings. Unlike TestRail there is no per-org instance URL to
 * type: Xray Cloud runs one global API host plus three regional ones, and
 * identity allowlists exactly those, so the form exposes a fixed Region
 * choice instead of a free-text URL. Global is the server-side default — a
 * regional base is only sent for an explicit region choice. The region picker
 * is a native `<select>` rather than the Radix one — only four fixed choices,
 * so the extra dependency would be overkill.
 */

interface FormErrors {
  clientId?: string;
  clientSecret?: string;
  submit?: string;
}

/** Shown under whichever Paste button the browser refused the clipboard to. */
const CLIPBOARD_DENIED =
  'Clipboard access denied. Paste manually with Cmd/Ctrl+V.';

function validate(values: api.XrayCredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  if (!values.clientId) {
    errors.clientId = 'Client ID is required';
  } else if (values.clientId.length < 8) {
    errors.clientId = 'Client ID looks too short';
  }
  if (!values.clientSecret) {
    errors.clientSecret = 'Client secret is required';
  } else if (values.clientSecret.length < 8) {
    errors.clientSecret = 'Client secret looks too short';
  }
  return errors;
}

export function XrayCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [values, setValues] = useState<api.XrayCredentialsPayload>(() => ({
    clientId: '',
    clientSecret: '',
    region: 'global',
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  /** Which field's Paste button failed, so the message renders under that field and not the other. */
  const [pasteErrorField, setPasteErrorField] = useState<
    'clientId' | 'clientSecret' | null
  >(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const handleFieldChange =
    (field: 'clientId' | 'clientSecret') =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setValues((v) => ({ ...v, [field]: e.target.value }));
      if (errors[field]) setErrors((es) => ({ ...es, [field]: undefined }));
    };

  /**
   * Read the clipboard and stuff the value into one of the two secret fields. Kept as a button
   * alongside the input because users have reported the native Cmd/Ctrl+V occasionally not
   * landing in this specific input (suspected interaction with the surrounding Radix Dialog focus
   * scope / password-input quirks). The Clipboard API ask runs on a user gesture, so the browser
   * permission prompt is fine.
   */
  const handlePasteFromClipboard = async (
    field: 'clientId' | 'clientSecret',
  ): Promise<void> => {
    if (action.blocked()) return;
    setPasteErrorField(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((v) => ({ ...v, [field]: text.trim() }));
      if (errors[field]) setErrors((es) => ({ ...es, [field]: undefined }));
    } catch {
      setPasteErrorField(field);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const v = validate(values);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      if (explainUnavailableActions)
        notify.error(
          Object.values(v)[0] ?? 'Complete the required connection fields.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveXrayCredentials(values, workspaceId, details);
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      {/*
        Two things a first-time connector cannot guess, and both cause a dead end without them: this
        connection is theirs and reaches nobody else, so each person connects their own; and the key
        can only be issued by a Jira administrator — Xray has no self-service key. Together they mean
        the same admin key gets connected by each person who needs Xray, which is worth saying plainly
        rather than letting somebody wait for a setup that will never arrive.
      */}
      <p className="text-xs text-muted-foreground">
        This connection is yours — nobody else here uses it. Only a Jira
        administrator can issue the key, so ask them for one: Jira → Apps →
        Manage your apps → Xray → API Keys.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="xray-region" className="text-xs">
          Region
        </Label>
        {explainUnavailableActions ? (
          <ConnectionChoiceSelect
            id="xray-region"
            value={values.region}
            options={[
              { value: 'global', label: 'Global (default)' },
              { value: 'us', label: 'US' },
              { value: 'eu', label: 'EU' },
              { value: 'au', label: 'AU' },
            ]}
            blocked={action.blocked}
            onValueChange={(region) =>
              setValues((current) => ({
                ...current,
                region: region as api.XrayRegion,
              }))
            }
          />
        ) : (
          <select
            id="xray-region"
            value={values.region}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                region: e.target.value as api.XrayRegion,
              }))
            }
            className={cn(
              'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-elev-1 transition-[color,box-shadow] outline-none',
              'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <option value="global">Global (default)</option>
            <option value="us">US</option>
            <option value="eu">EU</option>
            <option value="au">AU</option>
          </select>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="xray-clientId" className="text-xs">
          Client ID
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="xray-clientId"
              type={showClientId ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Xray Cloud client id"
              value={values.clientId}
              onChange={handleFieldChange('clientId')}
              aria-invalid={Boolean(errors.clientId)}
              aria-describedby={
                errors.clientId ? 'xray-clientId-err' : undefined
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowClientId((s) => !s)}
              aria-label={showClientId ? 'Hide ID' : 'Show ID'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showClientId ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard('clientId')}
            aria-label="Paste ID from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {errors.clientId && (
          <p id="xray-clientId-err" className="text-xs text-destructive">
            {errors.clientId}
          </p>
        )}
        {pasteErrorField === 'clientId' && (
          <p className="text-[11px] text-destructive">{CLIPBOARD_DENIED}</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="xray-clientSecret" className="text-xs">
          Client secret
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="xray-clientSecret"
              type={showClientSecret ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Xray Cloud client secret"
              value={values.clientSecret}
              onChange={handleFieldChange('clientSecret')}
              aria-invalid={Boolean(errors.clientSecret)}
              aria-describedby={
                errors.clientSecret
                  ? 'xray-clientSecret-err'
                  : 'xray-clientSecret-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowClientSecret((s) => !s)}
              aria-label={showClientSecret ? 'Hide secret' : 'Show secret'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showClientSecret ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard('clientSecret')}
            aria-label="Paste secret from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteErrorField === 'clientSecret' && (
          <p className="text-[11px] text-destructive">{CLIPBOARD_DENIED}</p>
        )}
        {errors.clientSecret ? (
          <p id="xray-clientSecret-err" className="text-xs text-destructive">
            {errors.clientSecret}
          </p>
        ) : (
          <p
            id="xray-clientSecret-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            In Xray, open{' '}
            <span className="font-medium">Global Settings → API Keys</span> and
            generate a client id + secret pair. Paste both here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
